import { Button, Card, Container, Grid, Typography } from '@mui/material'
import React, { Fragment, useEffect, useState } from 'react'
import { PickingDiff } from '../../../interfaces/PickingValidate/PickingDiff'
import { Loading } from '../../../components/UI'
import { useStyles } from './styles'
import { PickingService } from '../../../redux/services/pickingService'
import { SnackBar } from '../../SnackBar/Snackbar'
import { PickingDiffFilter } from '../../../interfaces/PickingValidate/PickingDiffFilter'
import { PickingDiffSearch } from './PickingDiffSearch'
import { ConfirmAction } from './ConfirmAction'
import { Modal } from '../../Modal'
import {
  PickingValidateSkuDetail,
  SaveDifferencesDto,
  SaveDifferencesPositionDto
} from '../../../interfaces/Picking/Picking'
import { PickingDiffHeader } from './PickingDiffHeader'
import { PickingDiffTotals } from './PickingDiffTotals'
import { PickingDiffList } from './PickingDiffList'
import { useUser } from '../../../hooks/useUser'

interface PickingDiffComponentProps {
  skuDetails: PickingValidateSkuDetail
  orderCancellationRequest: boolean
  handleCloseModal: (savedData: boolean) => void
  typePickingRouteId: number
}

export const PickingDiffComponent: React.FC<PickingDiffComponentProps> = ({
  skuDetails,
  orderCancellationRequest,
  handleCloseModal,
  typePickingRouteId
}) => {
  const {
    userDataState: { userData }
  } = useUser()
  const serviceError = (message: string): void => {
    console.error(message)
    setIsLoading(false)
    notify(`Error en operación: ${message}`, 'error')
  }

  const classes = useStyles()
  const [reloads, setReloads] = useState<number>(0)
  const [rows, setRows] = useState<PickingDiff[]>([])
  const [totals, setTotals] = useState<PickingDiff>(new PickingDiff())
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [pickingService] = useState<PickingService>(
    new PickingService(serviceError)
  )
  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false)
  const [snackBarMessage, setSnackBarMessage] = useState<string>('')
  const [snackBarStatus, setSnackBarStatus] = useState<string>('')
  const [updatedIds, setUpdatedIds] = useState<string[]>([])
  const [filters, setFilters] = useState<PickingDiffFilter>(
    new PickingDiffFilter()
  )
  const [openConfirm, setOpenConfirm] = useState<boolean>(false)
  const [isLoadingConfirm, setLoadingConfirm] = useState<boolean>(false)

  const notify = (message: string, status: string) => {
    setSnackBarMessage(message)
    setSnackBarStatus(status)
    setSnackBarOpen(true)
  }

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true)
      let data = await pickingService.getSkuPositions(
        skuDetails.pickingRouteId,
        skuDetails.skuId
      )
      setIsLoading(false)
      if (!data) {
        data = []
      }
      setRows(data)
      setUpdatedIds([])
      setFilters(new PickingDiffFilter())
    }
    fetchData()
  }, [reloads])

  useEffect(() => {
    updateTotals()
  }, [rows])

  const updateTotals = () => {
    const newTotals = new PickingDiff()
    newTotals.realQty = skuDetails.realQty
    rows.forEach(function (position) {
      newTotals.pickedQty += position.pickedQty
      newTotals.pickedAdjustmentQty += position.pickedAdjustmentQty
      newTotals.differenceQty += position.differenceQty
      newTotals.reductionQty += position.reductionQty
      newTotals.commercialRejectiontQty += position.commercialRejectiontQty
    })
    setTotals(newTotals)
  }

  const handleUpdatePosition = (position: PickingDiff) => {
    const rowIndex = rows.findIndex(function (e) {
      return (
        e.positionId === position.positionId &&
        e.batch?.batchId === position.batch?.batchId
      )
    })
    const newRows = [...rows]
    newRows[rowIndex] = position
    setRows(newRows)
    logUpdate(position)
  }

  const logUpdate = (position: PickingDiff) => {
    const uniqueId = `${position.positionId}-${
      position.batch?.batchId || 'no-batch'
    }`
    let newUpdatedIds: string[]
    if (position.hasChanged()) {
      newUpdatedIds = updatedIds.concat(uniqueId)
    } else {
      newUpdatedIds = updatedIds.filter(function (value) {
        return value !== uniqueId
      })
    }
    setUpdatedIds(newUpdatedIds)
  }

  const getSavePayload = () => {
    const saveDifferencesDto: SaveDifferencesDto = {
      skuId: skuDetails.skuId,
      userId: userData?.userId!,
      pickingRouteId: skuDetails.pickingRouteId,
      pickingRouteDetailId: skuDetails.pickingRouteDetailId,
      differences: []
    }
    saveDifferencesDto.differences = rows.map((row: PickingDiff) => {
      const dto: SaveDifferencesPositionDto = {
        positionId: row.positionId,
        pickedAdjustmentQty: row.pickedAdjustmentQty,
        differenceQty: row.differenceQty,
        reductionQty: row.reductionQty,
        reservedQty: row.reservedQty,
        commercialRejectionQty: row.commercialRejectiontQty,
        batchId: row.batch?.batchId
      }
      return dto
    })
    return saveDifferencesDto
  }

  const handleSubmitConfirm = async () => {
    setLoadingConfirm(true)
    setIsLoading(true)
    const saveDifferencesDto = getSavePayload()
    const apiResponse = await pickingService.saveDifferences(saveDifferencesDto)
    setIsLoading(false)
    if (apiResponse.success) {
      handleCloseConfirm()
      setReloads(reloads + 1)
      notify('Información guardada exitosamente', 'success')
    } else {
      handleCloseConfirm()
      notify(`Error al guardar información: ${apiResponse.message}`, 'error')
    }
    setLoadingConfirm(false)
  }

  const handleCloseConfirm = () => {
    setOpenConfirm(false)
  }

  const handleSave = async () => {
    setOpenConfirm(true)
  }

  const handleSnackbar = (isOpen: boolean) => {
    setSnackBarOpen(isOpen)
  }

  const handleOnChangeFilter = (e: any) => {
    const name = e.target.name
    const value = e.target.value
    setFilters({
      ...filters,
      [name]: value
    })
  }

  return (
    <Fragment>
      <PickingDiffHeader
        skuDetails={skuDetails}
        orderCancellationRequest={orderCancellationRequest}
      />

      <PickingDiffTotals element={totals} />
      <header
        style={{
          position: 'sticky',
          top: 0,
          background: 'white',
          zIndex: 1
        }}
      >
        <Grid container direction='row' spacing={2}>
          <Grid item xs={12} sm={6} lg={6}>
            <PickingDiffSearch
              filters={filters}
              handleOnChangeFilter={handleOnChangeFilter}
            />
          </Grid>
          <Grid item xs={12} sm={3} lg={3}>
            <Button
              color='primary'
              size='small'
              variant='contained'
              fullWidth={true}
              onClick={handleSave}
              disabled={updatedIds.length <= 0 || isLoading}
              className={classes.actionButton}
            >
              Guardar
            </Button>
          </Grid>
          <Grid item xs={12} sm={3} lg={3}>
            <Button
              color='inherit'
              size='small'
              variant='contained'
              fullWidth={true}
              onClick={() => handleCloseModal(reloads > 0)}
              className={classes.actionButton}
            >
              Cancelar
            </Button>
          </Grid>
        </Grid>
      </header>

      <Container
        maxWidth='xl'
        style={{
          maxHeight: '60vh',
          paddingBottom: '20px',
          overflowY: 'auto'
        }}
      >
        <div className={classes.grid}>
          {isLoading && <Loading />}
          {!isLoading && (
            <PickingDiffList
              rows={rows}
              totals={totals}
              filters={filters}
              handleUpdateElement={handleUpdatePosition}
              typePickingRouteId={typePickingRouteId}
              orderCancellationRequest={orderCancellationRequest}
            />
          )}
          {!isLoading && rows.length <= 0 && (
            <Card style={{ display: 'flex' }}>
              <Typography variant='subtitle2'>Sin Datos</Typography>
            </Card>
          )}
        </div>
      </Container>
      <Modal
        funcClose={handleCloseConfirm}
        isOpen={openConfirm}
        disabled={isLoadingConfirm}
        size='XS'
      >
        <ConfirmAction
          handleCloseModal={handleCloseConfirm}
          handleSubmitAction={handleSubmitConfirm}
          confirmTitle='Todos los movimientos serán realizados al confirmar con el botón CONFIRMAR. Si va a completar una ruta primero realice el picking físico de los productos antes de apretar el botón CONFIRMAR.'
          confirmText='Confirmar'
          negateText='Volver atrás'
          isLoading={isLoadingConfirm}
        />
      </Modal>

      <SnackBar
        open={snackBarOpen}
        setOpen={handleSnackbar}
        message={snackBarMessage}
        status={snackBarStatus}
      ></SnackBar>
    </Fragment>
  )
}
